import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Leaf, ArrowRight, Sparkles, User, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PROFESSIONS = [
  "Sophrologue", "Naturopathe", "Hypnothérapeute", "Ostéopathe",
  "Réflexologue", "Coach bien-être", "Praticien Reiki", "Énergéticien",
  "Psychomotricien", "Diététicien", "Nutritionniste", "Psychothérapeute",
  "Massage bien-être", "Praticien EFT", "Autre",
];

export default function Onboarding() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [profile, setProfile] = useState({
    full_name: user?.full_name || "",
    profession: user?.profession || "Sophrologue",
    siret: user?.siret || "",
    ape_code: user?.ape_code || "",
    adeli_number: user?.adeli_number || "",
    vat_mention_article: user?.vat_mention_article || "293B",
    address: user?.address || "",
    phone: user?.phone || "",
    iban: user?.iban || "",
    default_session_price: user?.default_session_price || 60,
    urssaf_regime: user?.urssaf_regime || "SSI",
  });

  const finish = async () => {
    setError(""); setSaving(true);
    try {
      const payload = { ...profile };
      payload.default_session_price = parseFloat(payload.default_session_price) || 60;
      payload.onboarding_completed = true;
      await api.put("/auth/profile", payload);
      await refreshUser();
      toast.success("🎉 Cabinet prêt — voici votre tableau de bord !");
      navigate("/dashboard");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  const skipAll = async () => {
    setSaving(true);
    try {
      await api.put("/auth/profile", { onboarding_completed: true });
      await refreshUser();
      navigate("/dashboard");
    } catch { /* silent */ }
    setSaving(false);
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "var(--bg)" }} data-testid="onboarding-page">
      <nav className="px-6 sm:px-10 py-5 max-w-[900px] w-full mx-auto flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-2xl bg-sage flex items-center justify-center">
            <Leaf className="w-4 h-4 text-white" strokeWidth={1.6} />
          </div>
          <div>
            <div className="font-display text-sm">Mon Cabinet Bien-être</div>
            <div className="text-[11px] text-muted-soft -mt-0.5">par Capsule Patrimoine</div>
          </div>
        </div>
        <button onClick={skipAll} className="text-xs text-muted-soft hover:text-sage transition-colors" data-testid="onb-skip-all">
          Passer cette étape →
        </button>
      </nav>

      <main className="flex-1 px-6 sm:px-10 max-w-[720px] w-full mx-auto pb-16">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sage-light mb-3">
            <Sparkles className="w-3.5 h-3.5 text-sage" />
            <span className="text-xs text-sage font-medium">Bienvenue dans votre cabinet</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl mb-2">
            Bonjour {(user?.full_name || "").split(" ")[0] || "et bravo"} ! 🌿
          </h1>
          <p className="text-muted-soft max-w-md mx-auto text-sm">
            Une dernière étape (1 minute) et vous accédez à votre tableau de bord. Tout est modifiable plus tard dans Réglages.
          </p>
        </div>

        <div className="card-soft p-7" data-testid="onb-step-1">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-sage-light flex items-center justify-center">
              <User className="w-5 h-5 text-sage" />
            </div>
            <div>
              <div className="font-display text-xl">Votre profil cabinet</div>
              <div className="text-xs text-muted-soft">Ces informations apparaîtront sur vos factures et documents</div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Nom complet *">
              <input className="input-soft" value={profile.full_name}
                onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                data-testid="onb-name" autoFocus />
            </Field>
            <Field label="Profession">
              <select className="input-soft" value={profile.profession}
                onChange={(e) => setProfile({ ...profile, profession: e.target.value })}
                data-testid="onb-profession">
                {PROFESSIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Régime URSSAF (calcul des cotisations)">
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setProfile({ ...profile, urssaf_regime: "SSI" })}
                className={`px-3 py-2.5 rounded-xl text-sm transition-all border text-left ${
                  profile.urssaf_regime === "SSI" ? "bg-sage text-white border-sage" : "border-border hover:bg-sage-light"
                }`} data-testid="onb-regime-ssi">
                <div className="font-medium text-xs">SSI · 25,6 %</div>
                <div className="text-[10px] opacity-80 mt-0.5">Sophro, naturo, hypno, énergie…</div>
              </button>
              <button type="button" onClick={() => setProfile({ ...profile, urssaf_regime: "CIPAV" })}
                className={`px-3 py-2.5 rounded-xl text-sm transition-all border text-left ${
                  profile.urssaf_regime === "CIPAV" ? "bg-sage text-white border-sage" : "border-border hover:bg-sage-light"
                }`} data-testid="onb-regime-cipav">
                <div className="font-medium text-xs">CIPAV · 23,2 %</div>
                <div className="text-[10px] opacity-80 mt-0.5">Ostéo, psychomot, diététicien…</div>
              </button>
            </div>
          </Field>

          <Field label="Tarif standard d'une séance (sera pré-rempli partout)">
            <div className="relative">
              <input type="number" min="0" step="1" className="input-soft pr-10"
                value={profile.default_session_price}
                onChange={(e) => setProfile({ ...profile, default_session_price: e.target.value })}
                data-testid="onb-price" />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-soft text-sm">€</span>
            </div>
          </Field>

          <Field label="Adresse professionnelle (apparaîtra sur vos factures)">
            <input className="input-soft" placeholder="12 rue de la Paix, 75002 Paris" value={profile.address}
              onChange={(e) => setProfile({ ...profile, address: e.target.value })}
              data-testid="onb-address" />
          </Field>

          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="SIRET (obligatoire pour vos factures)">
              <input className="input-soft" placeholder="14 chiffres" value={profile.siret}
                onChange={(e) => setProfile({ ...profile, siret: e.target.value })}
                data-testid="onb-siret" />
            </Field>
            <Field label="Téléphone (optionnel)">
              <input className="input-soft" placeholder="06..." value={profile.phone}
                onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                data-testid="onb-phone" />
            </Field>
          </div>

          <Field label="Numéro ADELI ou RPPS (si vous en avez un)">
            <input className="input-soft" placeholder="Ex : 759912345"
              value={profile.adeli_number}
              onChange={(e) => setProfile({ ...profile, adeli_number: e.target.value })}
              data-testid="onb-adeli" />
          </Field>

          <Field label="Mention de TVA sur vos factures">
            <select className="input-soft"
              value={profile.vat_mention_article}
              onChange={(e) => setProfile({ ...profile, vat_mention_article: e.target.value })}
              data-testid="onb-vat-article">
              <option value="293B">TVA non applicable, article 293 B du CGI</option>
              <option value="261-4-1">TVA non applicable, article 261-4-1° du CGI</option>
            </select>
            <div className="text-[11px] text-muted-soft mt-1.5 leading-relaxed">
              Vous avez un numéro ADELI/RPPS (profession de santé réglementée&nbsp;: ostéopathe,
              psychologue, psychothérapeute diplômé, chiropracteur)&nbsp;? Choisissez <b>261-4-1°</b>.
              Sinon (sophrologue, naturopathe, hypnothérapeute, réflexologue, coach bien-être…),
              choisissez <b>293 B</b>.
            </div>
          </Field>

          {error && <div className="text-sm text-red-500 mt-3" data-testid="onb-error">{error}</div>}

          <div className="flex justify-end mt-6">
            <button onClick={finish} disabled={saving || !profile.full_name}
              className="btn-primary inline-flex items-center gap-2" data-testid="onb-step1-next">
              {saving ? "Enregistrement…" : <>Accéder à mon tableau de bord <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mt-3">
      <span className="text-xs text-muted-soft mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
